import { useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SolveRecord {
  id: string;
  query: string;
  timeSec: number;
  success: boolean;
  podCount: number;
  timestamp: Date;
}

interface SpeedStatsPanelProps {
  records: SolveRecord[];
  onClose: () => void;
  onClear: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTime(sec: number): string {
  if (sec < 60) return `${sec.toFixed(1)}s`;
  return `${Math.floor(sec / 60)}m ${(sec % 60).toFixed(0)}s`;
}

function gradeTime(sec: number): { label: string; color: string } {
  if (sec <= 5) return { label: 'Instant', color: 'text-emerald-600 dark:text-emerald-400' };
  if (sec <= 15) return { label: 'Fast', color: 'text-teal-600 dark:text-teal-400' };
  if (sec <= 30) return { label: 'Normal', color: 'text-amber-600 dark:text-amber-400' };
  if (sec <= 60) return { label: 'Slow', color: 'text-orange-600 dark:text-orange-400' };
  return { label: 'Very Slow', color: 'text-rose-600 dark:text-rose-400' };
}

// ─── Bar Chart ────────────────────────────────────────────────────────────────

function BarChart({ records }: { records: SolveRecord[] }) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const recent = records.slice(-30);
  const maxTime = Math.max(...recent.map(r => r.timeSec), 1);

  if (recent.length === 0) {
    return (
      <div className="flex items-center justify-center h-36 text-sm text-slate-400 dark:text-slate-500">
        No solve records yet
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Y-axis labels */}
      <div className="flex items-end gap-1 h-36 relative">
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="w-full border-t border-dashed border-slate-200 dark:border-slate-700/50 flex items-center">
              <span className="text-[9px] text-slate-400 dark:text-slate-500 pr-1 bg-transparent leading-none">
                {i === 0 ? formatTime(maxTime) : i === 1 ? formatTime(maxTime * 0.66) : i === 2 ? formatTime(maxTime * 0.33) : '0s'}
              </span>
            </div>
          ))}
        </div>

        {/* Bars */}
        <div className="flex items-end gap-0.5 flex-1 h-full pl-10 relative">
          {recent.map((r, i) => {
            const heightPct = Math.max((r.timeSec / maxTime) * 100, 4);
            const isHovered = hoveredIdx === i;
            return (
              <div
                key={r.id}
                className="flex-1 relative group cursor-default"
                style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <div
                  className={`w-full rounded-t transition-all duration-150 ${
                    r.success
                      ? isHovered ? 'bg-emerald-400 dark:bg-emerald-400' : 'bg-emerald-500 dark:bg-emerald-500'
                      : isHovered ? 'bg-rose-400 dark:bg-rose-400' : 'bg-rose-500 dark:bg-rose-500'
                  }`}
                  style={{ height: `${heightPct}%` }}
                ></div>

                {/* Tooltip */}
                {isHovered && (
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-[10px] rounded-lg px-2.5 py-2 whitespace-nowrap z-10 pointer-events-none shadow-xl">
                    <div className="font-bold">{formatTime(r.timeSec)}</div>
                    <div className="text-slate-300 dark:text-slate-600 truncate max-w-[140px]">{r.query}</div>
                    <div className={r.success ? 'text-emerald-300 dark:text-emerald-600 font-semibold' : 'text-rose-300 dark:text-rose-600 font-semibold'}>
                      {r.success ? '✓ Success' : '✗ Failed'}
                    </div>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900 dark:border-t-slate-100"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* X-axis: sequence labels */}
      <div className="flex pl-10 mt-1 gap-0.5">
        {recent.map((_, i) => (
          i % Math.max(1, Math.floor(recent.length / 6)) === 0 ? (
            <div key={i} className="flex-1 text-center text-[8px] text-slate-400 dark:text-slate-500">{i + 1}</div>
          ) : <div key={i} className="flex-1"></div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SpeedStatsPanel({ records, onClose, onClear }: SpeedStatsPanelProps) {
  const [filter, setFilter] = useState<'all' | 'success' | 'failed'>('all');

  const successRecords = records.filter(r => r.success);
  const failedRecords = records.filter(r => !r.success);
  const accuracy = records.length > 0 ? Math.round((successRecords.length / records.length) * 100) : 0;
  const avgTime = successRecords.length > 0
    ? successRecords.reduce((s, r) => s + r.timeSec, 0) / successRecords.length
    : 0;
  const fastest = successRecords.length > 0 ? Math.min(...successRecords.map(r => r.timeSec)) : 0;
  const slowest = successRecords.length > 0 ? Math.max(...successRecords.map(r => r.timeSec)) : 0;

  const filtered = records.filter(r => {
    if (filter === 'success') return r.success;
    if (filter === 'failed') return !r.success;
    return true;
  }).slice().reverse();

  const speedGrade = avgTime > 0 ? gradeTime(avgTime) : null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-sky-600 to-cyan-600 px-7 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="ri-speed-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="font-bold text-white text-lg">Speed Stats</h2>
              <p className="text-sky-100 text-xs">{records.length} total solve{records.length !== 1 ? 's' : ''} recorded</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {records.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-8">
              <div className="w-20 h-20 bg-sky-50 dark:bg-sky-900/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <i className="ri-timer-line text-4xl text-sky-400"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">No Records Yet</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Enable Timer Mode with the <strong>⏱ Timer</strong> toggle, then solve problems to track your speed.
              </p>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  {
                    label: 'Accuracy',
                    value: `${accuracy}%`,
                    sub: `${successRecords.length} / ${records.length}`,
                    color: accuracy >= 80 ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50' : accuracy >= 60 ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50' : 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800/50',
                    textColor: accuracy >= 80 ? 'text-emerald-700 dark:text-emerald-300' : accuracy >= 60 ? 'text-amber-700 dark:text-amber-300' : 'text-rose-700 dark:text-rose-300',
                  },
                  {
                    label: 'Avg Speed',
                    value: avgTime > 0 ? formatTime(avgTime) : '—',
                    sub: speedGrade?.label ?? '',
                    color: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800/50',
                    textColor: speedGrade?.color ?? 'text-sky-700 dark:text-sky-300',
                  },
                  {
                    label: 'Fastest',
                    value: fastest > 0 ? formatTime(fastest) : '—',
                    sub: 'best solve time',
                    color: 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800/50',
                    textColor: 'text-violet-700 dark:text-violet-300',
                  },
                  {
                    label: 'Slowest',
                    value: slowest > 0 ? formatTime(slowest) : '—',
                    sub: 'longest solve',
                    color: 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700',
                    textColor: 'text-slate-600 dark:text-slate-300',
                  },
                ].map(stat => (
                  <div key={stat.label} className={`rounded-xl border p-4 ${stat.color}`}>
                    <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">{stat.label}</div>
                    <div className={`text-2xl font-black leading-none ${stat.textColor}`}>{stat.value}</div>
                    <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">{stat.sub}</div>
                  </div>
                ))}
              </div>

              {/* Bar Chart */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Solve Time History</h3>
                  <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block"></span>
                      Success
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-sm bg-rose-500 inline-block"></span>
                      Failed
                    </span>
                  </div>
                </div>
                <BarChart records={records} />
              </div>

              {/* Recent Solves List */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Recent Solves</h3>
                  <div className="flex gap-1">
                    {(['all', 'success', 'failed'] as const).map(f => (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer capitalize whitespace-nowrap ${
                          filter === f
                            ? f === 'success' ? 'bg-emerald-600 text-white'
                              : f === 'failed' ? 'bg-rose-600 text-white'
                              : 'bg-slate-700 dark:bg-slate-200 text-white dark:text-slate-800'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {filtered.length === 0 ? (
                    <div className="text-center py-8 text-sm text-slate-400 dark:text-slate-500">No records for this filter</div>
                  ) : (
                    filtered.slice(0, 20).map(r => {
                      const grade = gradeTime(r.timeSec);
                      return (
                        <div
                          key={r.id}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
                            r.success
                              ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/50'
                              : 'bg-rose-50 dark:bg-rose-900/10 border-rose-200 dark:border-rose-800/50'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-xs ${r.success ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                            <i className={r.success ? 'ri-check-line' : 'ri-close-line'}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-mono font-semibold text-slate-800 dark:text-slate-100 truncate">{r.query}</div>
                            <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                              {r.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {r.podCount} pods
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className={`font-bold text-sm ${grade.color}`}>{formatTime(r.timeSec)}</div>
                            <div className={`text-[10px] font-semibold ${grade.color}`}>{grade.label}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
          <button
            onClick={onClear}
            className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-5-line mr-1"></i>
            Clear Records
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
